"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Gift, Sparkles, X } from "lucide-react";
import { useLocale, useT } from "@/lib/use-i18n";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useFocusTrapDialog } from "@/components/chat/hooks/use-focus-trap-dialog";
import { localePath } from "@/lib/i18n/locales";
import { useAuth } from "@/lib/auth-context";
import { fetchUsageShared } from "@/components/chat/hooks/use-chat-quotas";
import type { GiftPlan } from "@/lib/gift-subscription";
import { getPromoCodeDisplayPlan } from "@/lib/promo-codes";

interface GiftNoticeDto {
  id: string;
  // RESUELTO 2026-09-12 [GENERAL-B-P3] DTO alinéado con GiftPlan (go/pro/max/maxplus) |fuente gift-subscription.ts:8
  plan: GiftPlan;
  days: number;
  grantedAt: number;
  dismissed: boolean;
  reason: string;
}

/**
 * Modal thank-you for gifted plans. Only shows for signed-in users with
 * an undismissed gift_notice on /api/usage.
 */
export function GiftProNotice() {
  const t = useT();
  const locale = useLocale();
  const router = useRouter();
  const { user } = useAuth();
  const [notice, setNotice] = useState<GiftNoticeDto | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const fetchedUserRef = useRef<string | null>(null);
  const userId = user?.id ?? null;
  // 2026-09-13 canónico animaciones: entrada/salida animate-in/out
  // (paridad CookieConsent — solo transform+opacity, 150ms).
  const { shouldRender: shouldRenderModal, isLeaving: isLeavingModal } =
    useAnimatedOpen(open, 150);

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      fetchedUserRef.current = null;
      // Defer clear so we don't setState synchronously inside the effect body.
      const clearTimer = setTimeout(() => {
        if (!cancelled) {
          setNotice(null);
          setOpen(false);
        }
      }, 0);
      return () => {
        cancelled = true;
        clearTimeout(clearTimer);
      };
    }

    if (fetchedUserRef.current === userId) return;
    fetchedUserRef.current = userId;

    (async () => {
      try {
        // 2026-09-19 perf-cpu (owner compartido): via fetchUsageShared — el
        // mismo inflight + ventana 30s que use-chat-quotas. Sin fetch propio
        // con no-store: respeta private max-age=20; el regalo cambia por
        // grant explícito, 20s de staleness no oculta nada. One-shot por
        // sign-in (no poll), exento de regla intervalo con este comentario.
        const data = await fetchUsageShared(false);
        // Dedup hit (chat quotas ya pidió <30s) pero este userId aún no se
        // checked: un force puntual (1× por sign-in, comparte inflight) evita
        // perder el regalo por montar tarde (dynamic import).
        const resolved = data ?? (await fetchUsageShared(true));
        if (!resolved || cancelled) return;
        const g = resolved.giftNotice as GiftNoticeDto | null | undefined;
        if (g && !g.dismissed && g.days > 0 && !cancelled) {
          setNotice(g);
          setOpen(true);
        }
      } catch {
        // Network blip — don't block the app.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const dismiss = useCallback(async () => {
    if (dismissing) return;
    setDismissing(true);
    setOpen(false);
    try {
      await fetch("/api/account/gift-notice/dismiss", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: "{}",
      });
    } catch {
      // Still hide locally; next visit may re-show until server accepts.
    } finally {
      setDismissing(false);
      setNotice(null);
    }
  }, [dismissing]);

  // "Ver mi plan" → dismiss the notice and open the dashboard plan section.
  const handleCta = useCallback(() => {
    void dismiss();
    router.push(localePath(locale, "/dashboard"));
  }, [dismiss, locale, router]);

  // 2026-09-13 a11y lote 2.1: foco inicial + trap Tab + Escape + retorno
  // al trigger (patrón chat-sidebar lightbox vía useFocusTrapDialog).
  const dialogRef = useFocusTrapDialog(open, dismiss);

  if (!shouldRenderModal || !notice) return null;

  // RESUELTO 2026-09-12 [GENERAL-B-P3] etiqueta vía SSOT (antes "Pro" para go/maxplus) |fuente promo-codes.ts:39
  const planLabel = getPromoCodeDisplayPlan(notice.plan);

  // 2026-09-11 heroic exception → 2026-09-23 FASE 3 FIXER diseño H5: el
  // emerald/teal literal migra a --success semántico SSOT (1 brillo, sin
  // teal): éxito, no chrome decorativo. Contraste AA: --success-text sobre
  // --card supera 4.5:1 en ambos temas. i18n intacto (cero copy).
  return (
    <div
      className="vortixy-safe-area fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gift-pro-title"
      aria-describedby="gift-pro-body"
    >
      <button
        type="button"
        // 2026-09-23 FASE 3 FIXER diseño H1 (research 2026 MDN backdrop-filter
        // 2026-04-20: blur trasero es capa GPU + glass multi-capa — dim opaco
        // bg-black/60 basta (test modal-opacity exige ≥60); panel modal-surface
        // opaco, 0 blurs en el diálogo).
        className="absolute inset-0 bg-black/60"
        aria-label={t("giftProClose")}
        onClick={dismiss}
      />

      <div
        ref={dialogRef}
        role="document"
        // 2026-09-16 fondo opaco: gradient inline opaco — .lovable-card
        // unlayered (dark 75% + blur) vencía al utility bg-gradient layerado
        // y el chat se transparentaba detrás del modal.
        // 2026-09-23 FASE 3 FIX diseño system-tokens (research 2026 W3C DTCG scoped
        // theming + WCAG 2.2 1.4.3: el gradient hard-dark #1a2420/#141413 dejaba texto
        // zinc ilegible en light — fondo por token --card (resuelve warm-paper en light,
        // charcoal en dark); acentos emerald semánticos intactos, tinta por tokens).
        style={{
          background: "var(--card)",
        }}
        className={`lovable-card lovable-card--static modal-surface relative w-full max-w-md overflow-hidden rounded-[var(--radius-panel)] border-[var(--success)]/30 shadow-[0_25px_80px_-20px_color-mix(in_srgb,var(--success)_45%,transparent)] ${isLeavingModal ? "animate-out fade-out zoom-out-95 duration-150 motion-reduce:animate-none" : "animate-in fade-in zoom-in-95 duration-150 motion-reduce:animate-none"}`}
      >
        {/* 2026-09-19 FASE 3 FIXER (Lovable sin blur-2xl/3xl): brillos
            sólidos a baja opacidad, sin blur.
            2026-09-23 FASE 3 FIXER diseño H5 (research 2026 DSP single-source
            tokens: emerald-500/teal-400 literales → --success semántico SSOT;
            2 brillos → 1 brillo; --accent monocromo para chrome). */}
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-[var(--success)]/10"
          aria-hidden
        />

        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition hover:bg-[var(--muted)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          aria-label={t("giftProClose")}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>

        <div className="relative space-y-4 px-6 pb-6 pt-8 sm:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-panel)] bg-[var(--success)]/15 ring-1 ring-[var(--success)]/30">
              <Gift
                className="h-6 w-6 text-[var(--success-text)]"
                aria-hidden
              />
            </div>
            <div className="min-w-0">
              {/* 2026-09-24 FASE 7 tipografía: badge secundario 11px fijo +
                  tracking cap 0.08em + truncate (antes 12px + 0.14em que
                  competía con título primario text-lg semibold). */}
              <p
                className="truncate text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--success-text)]"
                title={t("giftProBadge")}
              >
                {t("giftProBadge")}
              </p>
              <h2
                id="gift-pro-title"
                className="text-lg font-semibold tracking-tight text-foreground"
              >
                {t("giftProTitle", {
                  plan: planLabel,
                  days: String(notice.days),
                })}
              </h2>
            </div>
          </div>

          <p
            id="gift-pro-body"
            className="text-[15px] leading-relaxed text-foreground-soft"
          >
            {t("giftProBody", { plan: planLabel, days: String(notice.days) })}
          </p>

          <ul className="space-y-2 rounded-[var(--radius-panel)] border border-[var(--border)] bg-[var(--muted)]/40 px-4 py-3 text-sm text-foreground-soft">
            <li className="flex items-start gap-2">
              <Sparkles
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success-text)]"
                aria-hidden
              />
              <span>{t("giftProBulletQuota")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success-text)]"
                aria-hidden
              />
              <span>{t("giftProBulletModes")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--success-text)]"
                aria-hidden
              />
              <span>
                {t("giftProBulletDays", { days: String(notice.days) })}
              </span>
            </li>
          </ul>

          <button
            type="button"
            onClick={handleCta}
            disabled={dismissing}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--success)] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 disabled:opacity-70"
          >
            {t("giftProCta")}
          </button>
        </div>
      </div>
    </div>
  );
}
